import { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Send, Paperclip, Reply, Copy, Pencil, Trash2, X, Check, CheckCheck, FileText, Sparkles, Mic, Square } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dueInfo, TaskForm } from "./TasksPanel";

const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.ceil(b / 1024)} KB`);

const Attachment = ({ att }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let url;
    if (att.kind === "image" || att.kind === "video" || att.kind === "audio") {
      api.get(`/tasks/files/${att.path}`, { responseType: "blob" })
        .then((r) => { url = URL.createObjectURL(r.data); setBlobUrl(url); }).catch(() => {});
    }
    return () => url && URL.revokeObjectURL(url);
  }, [att.path, att.kind]);
  const download = async () => {
    try {
      const r = await api.get(`/tasks/files/${att.path}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u; a.download = att.name; a.click();
      URL.revokeObjectURL(u);
    } catch { toast.error("Download failed"); }
  };
  if (att.kind === "image") return blobUrl ? <img src={blobUrl} alt={att.name} onClick={download} className="rounded-lg max-h-48 cursor-pointer" /> : <div className="h-24 w-36 bg-slate-200 rounded-lg animate-pulse" />;
  if (att.kind === "video") return blobUrl ? <video src={blobUrl} controls className="rounded-lg max-h-48 max-w-full" /> : <div className="h-24 w-36 bg-slate-200 rounded-lg animate-pulse" />;
  if (att.kind === "audio") return blobUrl ? (
    <span className="flex items-center gap-1.5"><Mic className="h-4 w-4 text-blue-600 shrink-0" /><audio src={blobUrl} controls className="h-9 max-w-[210px]" /></span>
  ) : <div className="h-9 w-44 bg-slate-200 rounded-full animate-pulse" />;
  return (
    <button onClick={download} className="flex items-center gap-2 bg-slate-100 rounded-lg px-2.5 py-2 text-left hover:bg-slate-200 max-w-full">
      <FileText className="h-6 w-6 text-blue-600 shrink-0" />
      <span className="min-w-0"><span className="block text-xs font-semibold text-slate-800 truncate">{att.name}</span>
        <span className="text-[10px] text-slate-500">{fmtSize(att.size)} · tap to download</span></span>
    </button>
  );
};

const MsgText = ({ m }) => {
  let nodes = [m.text];
  [...(m.mentions || [])].sort((a, b) => b.name.length - a.name.length).forEach(({ name }, mi) => {
    nodes = nodes.flatMap((n, ni) => {
      if (typeof n !== "string") return [n];
      const segs = n.split(`@${name}`);
      return segs.flatMap((seg, i) => i < segs.length - 1
        ? [seg, <span key={`m${mi}-${ni}-${i}`} className="text-blue-600 font-bold">@{name}</span>] : [seg]);
    });
  });
  return <p className="text-[13px] text-slate-800 whitespace-pre-wrap break-words">{nodes}</p>;
};

export const TaskChat = ({ taskId, people, onBack }) => {
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [reads, setReads] = useState({});
  const [parts, setParts] = useState([]);
  const [input, setInput] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);
  const [menuFor, setMenuFor] = useState(null);
  const [upload, setUpload] = useState(null);
  const [editTask, setEditTask] = useState(false);
  const [mentionQ, setMentionQ] = useState(null);
  const mentionsRef = useRef({});
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const recRef = useRef(null);
  const recTimer = useRef(null);
  const cancelRef = useRef(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const touchX = useRef(0);
  const me = user?.email;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tasks/${taskId}/messages`);
      setTask(data.task); setMsgs(data.messages); setReads(data.reads); setParts(data.participants);
    } catch { /* ignore */ }
  }, [taskId]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  useEffect(() => () => { clearInterval(recTimer.current); if (recRef.current?.state === "recording") { cancelRef.current = true; recRef.current.stop(); } }, []);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    setMentionQ(null);
    try {
      if (editing) {
        await api.patch(`/tasks/${taskId}/messages/${editing.id}`, { text });
        setEditing(null);
      } else {
        const mentions = Object.entries(mentionsRef.current)
          .filter(([name]) => text.includes(`@${name}`))
          .map(([name, email]) => ({ name, email }));
        await api.post(`/tasks/${taskId}/messages`, { text, reply_to_id: replyTo?.id || null, mentions });
        setReplyTo(null);
        mentionsRef.current = {};
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) uploadFile(f);
  };

  const uploadFile = async (f) => {
    if (f.size > 25 * 1024 * 1024) return toast.error("Max 25 MB per file");
    const fd = new FormData();
    fd.append("file", f);
    setUpload({ name: f.name, pct: 0 });
    try {
      await api.post(`/tasks/${taskId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => setUpload({ name: f.name, pct: Math.round((ev.loaded / (ev.total || f.size)) * 100) }),
      });
      load();
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Upload failed"); }
    setUpload(null);
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "");
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      mr.ondataavailable = (ev) => ev.data.size && chunks.push(ev.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (!cancelRef.current && chunks.length) {
          const type = mr.mimeType || "audio/webm";
          const ext = type.includes("mp4") ? "m4a" : "webm";
          uploadFile(new File([new Blob(chunks, { type })], `Voice note.${ext}`, { type }));
        }
      };
      cancelRef.current = false;
      mr.start();
      recRef.current = mr;
      setRecSecs(0);
      recTimer.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
      setRecording(true);
    } catch { toast.error("Microphone access denied"); }
  };

  const stopRec = (cancel) => {
    cancelRef.current = cancel;
    clearInterval(recTimer.current);
    setRecording(false);
    recRef.current?.state !== "inactive" && recRef.current?.stop();
  };

  const act = async (fn, ok) => { try { await fn(); ok && toast.success(ok); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const copyMsg = (m) => { navigator.clipboard.writeText(m.text); toast.success("Copied"); setMenuFor(null); };
  const delMsg = (m) => { if (window.confirm("Delete this message?")) act(() => api.delete(`/tasks/${taskId}/messages/${m.id}`)); setMenuFor(null); };
  const startEdit = (m) => { setEditing(m); setInput(m.text); setReplyTo(null); setMenuFor(null); };
  const seenByAll = (m) => parts.filter((p) => p !== me).every((p) => reads[p] && reads[p] >= m.created_at);

  if (!task) return <div className="flex-1 flex items-center justify-center text-slate-400 text-sm bg-white">Loading…</div>;
  const d = dueInfo(task.due_at, task.status);
  const isCreator = me === task.created_by || user?.role === "admin";
  const closed = task.status === "closed";
  const chatPeople = [{ email: task.created_by, name: task.created_by_name || task.created_by.split("@")[0] },
    ...task.assignees.map((a) => ({ email: a.email, name: a.name || a.email.split("@")[0] }))]
    .filter((p, i, arr) => arr.findIndex((x) => x.email === p.email) === i);
  const mentionMatches = mentionQ != null
    ? chatPeople.filter((p) => p.email !== me && p.name.toLowerCase().startsWith(mentionQ.toLowerCase()))
    : [];
  const onInputChange = (v) => {
    setInput(v);
    const mm = v.match(/@([^\s@]*)$/);
    setMentionQ(mm ? mm[1] : null);
  };
  const pickMention = (p) => {
    setInput((v) => v.replace(/@([^\s@]*)$/, `@${p.name} `));
    mentionsRef.current[p.name] = p.email;
    setMentionQ(null);
  };

  if (editTask) return (
    <TaskForm people={people?.length ? people : chatPeople} initial={task} submitLabel="Save Changes"
      onBack={() => setEditTask(false)}
      onSubmit={async (payload) => {
        await api.patch(`/tasks/${taskId}`, payload);
        toast.success("Task updated");
        setEditTask(false);
        load();
      }} />
  );

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-[#EFF4FB]">
      <div className="bg-blue-700 text-white px-3 py-2 flex items-center gap-2">
        <button data-testid="chat-back" onClick={onBack}><ArrowLeft className="h-4 w-4" /></button>
        <span className={`w-9 h-9 rounded-lg flex flex-col items-center justify-center shrink-0 ${d.cls}`}>
          <span className="text-[8px] font-bold leading-tight">{d.top}</span><span className="text-[9px] font-extrabold leading-tight">{d.bottom}</span>
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold truncate">{task.headline}</p>
          <p className="text-[10px] text-blue-100 truncate">
            {task.due_at ? `Due ${new Date(task.due_at).toLocaleString([], { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })} · ` : "No due date · "}
            {task.assignees.map((a) => `${a.name || a.email}${a.role === "primary" ? " (P)" : " (S)"}`).join(", ")}
          </p>
        </div>
        {!closed && isCreator && (
          <button data-testid="task-edit-btn" onClick={() => setEditTask(true)} title="Edit task"
            className="p-1.5 rounded-lg hover:bg-blue-600"><Pencil className="h-3.5 w-3.5" /></button>
        )}
        {!closed && (isCreator ? (
          <Button data-testid="task-close-btn" size="sm" onClick={() => window.confirm("Close this task?") && act(() => api.post(`/tasks/${taskId}/close`), "Task closed")}
            className="h-7 text-[10px] bg-white text-blue-700 hover:bg-blue-50 px-2">Close Task</Button>
        ) : (
          <Button data-testid="task-reqclose-btn" size="sm" onClick={() => act(() => api.post(`/tasks/${taskId}/request-close`), "Close requested")}
            disabled={task.status === "close_requested"} className="h-7 text-[10px] bg-white text-blue-700 hover:bg-blue-50 px-2">
            {task.status === "close_requested" ? "Requested" : "Request Close"}</Button>
        ))}
      </div>
      {task.description && (
        <div className="bg-white border-b border-slate-200 px-3 py-2 flex items-start justify-between gap-2">
          <p className="text-xs text-slate-600">{task.description}</p>
          <span className={`shrink-0 text-[9px] font-bold px-2 py-0.5 rounded-full ${closed ? "bg-slate-200 text-slate-600" : task.status === "close_requested" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
            {closed ? "Closed" : task.status === "close_requested" ? "Close Requested" : "Open"}</span>
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {msgs.map((m) => {
          if (m.type === "system") return <p key={m.id} className="text-center"><span className="inline-block bg-slate-200/80 text-slate-600 text-[10px] px-3 py-1 rounded-full">{m.text}</span></p>;
          if (m.type === "ai") return (
            <p key={m.id} className="text-center"><span className="inline-block bg-violet-100 border border-violet-200 text-violet-800 text-[11px] px-3 py-1.5 rounded-xl max-w-[85%]">
              <Sparkles className="h-3 w-3 inline mr-1 text-violet-500" />{m.text}<span className="block text-[9px] text-violet-400 mt-0.5">{fmtTime(m.created_at)}</span></span></p>
          );
          const mine = m.sender_email === me;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}
              onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => { if (!m.deleted && touchX.current - e.changedTouches[0].clientX > 50) setReplyTo(m); }}>
              <div className={`relative group max-w-[80%] rounded-xl px-2.5 py-1.5 shadow-sm ${mine ? "bg-[#D7E8FF]" : "bg-white"}`}
                onClick={() => setMenuFor(menuFor === m.id ? null : m.id)}>
                {!mine && <p className="text-[10px] font-bold text-blue-600">{m.sender_name}</p>}
                {m.reply_to && (
                  <div className="border-l-2 border-blue-500 bg-black/5 rounded px-2 py-1 mb-1">
                    <p className="text-[9px] font-bold text-blue-600">{m.reply_to.sender_name}</p>
                    <p className="text-[10px] text-slate-500 truncate">{m.reply_to.snippet}</p>
                  </div>
                )}
                {m.deleted ? <p className="text-xs italic text-slate-400">🚫 This message was deleted</p> : (
                  <>
                    {m.attachment && <div className="mb-1"><Attachment att={m.attachment} /></div>}
                    {m.text && <MsgText m={m} />}
                  </>
                )}
                <p className="text-[9px] text-slate-400 text-right flex items-center justify-end gap-0.5">
                  {m.edited_at && <span>edited · </span>}{fmtTime(m.created_at)}
                  {mine && !m.deleted && (seenByAll(m)
                    ? <CheckCheck className="h-3 w-3 text-blue-500" title={`Seen by ${parts.filter((p) => p !== me).join(", ")}`} />
                    : <Check className="h-3 w-3 text-slate-400" />)}
                </p>
                {menuFor === m.id && !m.deleted && !closed && (
                  <div className={`absolute z-20 top-full mt-1 ${mine ? "right-0" : "left-0"} bg-white rounded-lg shadow-lg border border-slate-200 flex`}>
                    <button data-testid={`msg-reply-${m.id}`} onClick={(e) => { e.stopPropagation(); setReplyTo(m); setMenuFor(null); }} className="p-2 hover:bg-slate-50" title="Reply"><Reply className="h-3.5 w-3.5 text-slate-600" /></button>
                    {m.text && <button data-testid={`msg-copy-${m.id}`} onClick={(e) => { e.stopPropagation(); copyMsg(m); }} className="p-2 hover:bg-slate-50" title="Copy"><Copy className="h-3.5 w-3.5 text-slate-600" /></button>}
                    {mine && m.text && <button data-testid={`msg-edit-${m.id}`} onClick={(e) => { e.stopPropagation(); startEdit(m); }} className="p-2 hover:bg-slate-50" title="Edit (15 min)"><Pencil className="h-3.5 w-3.5 text-slate-600" /></button>}
                    {mine && <button data-testid={`msg-delete-${m.id}`} onClick={(e) => { e.stopPropagation(); delMsg(m); }} className="p-2 hover:bg-slate-50" title="Delete (48h)"><Trash2 className="h-3.5 w-3.5 text-rose-500" /></button>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      {upload && (
        <div className="bg-white border-t border-slate-200 px-3 py-2">
          <p className="text-xs text-slate-600 truncate">📎 {upload.name}</p>
          <div className="h-1.5 bg-slate-200 rounded-full mt-1"><div className="h-1.5 bg-blue-600 rounded-full transition-all" style={{ width: `${upload.pct}%` }} /></div>
        </div>
      )}
      {(replyTo || editing) && (
        <div className="bg-white border-t border-slate-200 px-3 py-1.5 flex items-center justify-between">
          <div className="border-l-2 border-blue-500 pl-2 min-w-0">
            <p className="text-[10px] font-bold text-blue-600">{editing ? "Editing message" : `Replying to ${replyTo.sender_name}`}</p>
            <p className="text-[11px] text-slate-500 truncate">{(editing || replyTo).text || "📎 attachment"}</p>
          </div>
          <button onClick={() => { setReplyTo(null); setEditing(null); setInput(""); }}><X className="h-4 w-4 text-slate-400" /></button>
        </div>
      )}
      {!closed && (
        <div className="relative bg-white border-t border-slate-200 px-2 py-2 flex items-center gap-1.5">
          {mentionMatches.length > 0 && (
            <div data-testid="mention-dropdown" className="absolute bottom-full left-2 right-2 mb-1 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-30">
              {mentionMatches.slice(0, 5).map((p) => (
                <button key={p.email} data-testid={`mention-opt-${p.email}`} onClick={() => pickMention(p)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-blue-50 text-left">
                  <span className="w-7 h-7 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">{p.name[0]?.toUpperCase()}</span>
                  <span className="text-sm text-slate-800 font-medium">{p.name}</span>
                  <span className="text-[10px] text-slate-400">{p.email}</span>
                </button>
              ))}
            </div>
          )}
          <input ref={fileRef} type="file" className="hidden" onChange={pickFile}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*,video/*,audio/*" />
          {recording ? (
            <div data-testid="voice-recording-bar" className="flex-1 flex items-center gap-2 px-2">
              <span className="h-2.5 w-2.5 rounded-full bg-rose-500 animate-pulse" />
              <span className="text-sm font-mono text-slate-700">{Math.floor(recSecs / 60)}:{String(recSecs % 60).padStart(2, "0")}</span>
              <span className="text-xs text-slate-400 flex-1">Recording voice note…</span>
              <button data-testid="voice-cancel-btn" onClick={() => stopRec(true)} className="p-2 text-slate-500 hover:text-rose-600" title="Discard"><Trash2 className="h-4 w-4" /></button>
              <Button data-testid="voice-send-btn" onClick={() => stopRec(false)} className="h-9 w-9 p-0 rounded-full bg-blue-600 hover:bg-blue-700" title="Send voice note"><Send className="h-4 w-4" /></Button>
            </div>
          ) : (
          <>
          <button data-testid="chat-attach-btn" onClick={() => fileRef.current?.click()} disabled={!!upload}
            className="p-2 text-slate-500 hover:text-blue-600"><Paperclip style={{ height: 18, width: 18 }} /></button>
          <input data-testid="chat-input" value={input} onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Message…"
            className="flex-1 rounded-full border border-slate-300 px-3.5 py-2 text-sm text-slate-800 outline-none focus:border-blue-500" />
          {input.trim() ? (
            <Button data-testid="chat-send-btn" onClick={send} className="h-9 w-9 p-0 rounded-full bg-blue-600 hover:bg-blue-700"><Send className="h-4 w-4" /></Button>
          ) : (
            <Button data-testid="voice-rec-btn" onClick={startRec} disabled={!!upload} title="Record voice note"
              className="h-9 w-9 p-0 rounded-full bg-blue-600 hover:bg-blue-700"><Mic className="h-4 w-4" /></Button>
          )}
          </>
          )}
        </div>
      )}
      {closed && <p className="bg-white border-t border-slate-200 text-center text-xs text-slate-400 py-2.5">Task closed · chat is read-only</p>}
    </div>
  );
};
